import { SmgElement } from '../core/smg-element';
import { each, html, when, type SafeHtml } from '../core/safe-html';
import { initials, nfmt } from '../core/format';
import { navigate } from '../core/router';
import {
  applyTheme,
  THEME_ICONS,
  THEME_LABELS,
  themePreference,
  type ThemePreference,
} from '../core/theme';
import { ApiError } from '../models/common';
import type { Thresholds } from '../models/settings';
import { ROLE_LABELS } from '../models/user';
import { auth, session } from '../services/auth-service';
import { settings, settingsStore } from '../services/settings-service';
import { showToast } from '../services/toast-service';
import { users as userService } from '../services/user-service';

interface ThresholdRowDef {
  key: keyof Thresholds;
  label: string;
  icon: string;
}

const THRESHOLD_ROWS: ThresholdRowDef[] = [
  { key: 'temp', label: 'Sıcaklık (°C)', icon: 'ti ti-temperature' },
  { key: 'disk', label: 'Kök disk doluluk (%)', icon: 'ti ti-database' },
  { key: 'emmc', label: 'eMMC ömrü (JEDEC)', icon: 'ti ti-device-sd-card' },
];

/** Ayarlar: sağlık eşikleri (admin), oturum, salt-okunur WireGuard, görünüm. */
export class SettingsView extends SmgElement {
  private draft: Thresholds | null = null;
  private loading = true;
  private error = '';
  private pwOpen = false;
  private pwCurrent = '';
  private pwNext = '';
  private pwNext2 = '';

  protected override onConnected(): void {
    this.wireActs();
    void this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = '';
    this.render();
    try {
      const res = await settings.get();
      this.draft = structuredClone(res.thresholds);
    } catch {
      this.error = 'Ayarlar alınamadı.';
    } finally {
      this.loading = false;
      this.render();
    }
  }

  private wireActs(): void {
    this.onAct('click', {
      save: () => void this.save(),
      logout: () => void auth.logout().then(() => navigate('#/login')),
      theme: (el) => {
        applyTheme((el.dataset['pref'] ?? 'system') as ThemePreference);
        this.render();
      },
      openPw: () => {
        this.pwOpen = true;
        this.pwCurrent = this.pwNext = this.pwNext2 = '';
        this.render();
      },
      closePw: () => {
        this.pwOpen = false;
        this.render();
      },
      submitPw: () => void this.submitPw(),
      noop: (_el, ev) => ev.stopPropagation(),
    });
    this.onAct('input', {
      th: (el) => {
        const input = el as HTMLInputElement;
        const key = input.dataset['key'] as keyof Thresholds;
        const field = input.dataset['field'] as 'warn' | 'crit';
        if (this.draft && key && field) this.draft[key][field] = input.value;
      },
      pwCur: (el) => (this.pwCurrent = (el as HTMLInputElement).value),
      pwNew: (el) => (this.pwNext = (el as HTMLInputElement).value),
      pwNew2: (el) => (this.pwNext2 = (el as HTMLInputElement).value),
    });
  }

  private async submitPw(): Promise<void> {
    if (this.pwNext.length < 6) {
      showToast('Yeni parola en az 6 karakter olmalı', 'warning');
      return;
    }
    if (this.pwNext !== this.pwNext2) {
      showToast('Yeni parolalar eşleşmiyor', 'warning');
      return;
    }
    try {
      await userService.changeOwnPassword(this.pwCurrent, this.pwNext);
      this.pwOpen = false;
      showToast('Parolan değiştirildi — diğer oturumların kapatıldı');
      this.render();
    } catch (e) {
      showToast(
        e instanceof ApiError && e.status === 403 ? 'Mevcut parola hatalı' : 'Parola değiştirilemedi',
        'warning',
      );
    }
  }

  private async save(): Promise<void> {
    if (!this.draft) return;
    try {
      const saved = await settings.saveThresholds(this.draft);
      this.draft = structuredClone(saved);
      showToast('Eşikler kaydedildi');
      this.render();
    } catch {
      showToast('Kaydedilemedi', 'warning');
    }
  }

  protected override template(): SafeHtml {
    return html`
      <div class="scr">
        <div style="flex:none;background:var(--vx-surface);border-bottom:1px solid var(--vx-border);padding:16px 16px 14px">
          <div style="font:600 21px var(--vx-font-sans)">Ayarlar</div>
          <div style="font:400 12.5px var(--vx-font-sans);color:var(--vx-text-2);margin-top:2px">Sağlık eşikleri, oturum, WireGuard ve görünüm</div>
        </div>
        ${this.bodyTemplate()}
      </div>`;
  }

  private bodyTemplate(): SafeHtml {
    if (this.loading) {
      return html`<div class="body" style="display:flex;align-items:center;justify-content:center">
        <i class="ti ti-loader-2 spin" style="font-size:26px;color:var(--vx-primary)"></i></div>`;
    }
    if (this.error) {
      return html`<div class="body" style="display:flex;align-items:center;justify-content:center;padding:24px">
        <div style="text-align:center;color:var(--vx-text-2)">
          <i class="ti ti-plug-connected-x" style="font-size:30px;color:var(--vx-text-3)"></i>
          <div style="font:500 13.5px var(--vx-font-sans);margin-top:8px">${this.error}</div>
        </div></div>`;
    }
    return html`
      <div class="body" style="padding:16px;display:flex;flex-wrap:wrap;gap:16px;align-content:flex-start;overflow-x:hidden">
        ${this.thresholdsCard()}
        ${this.sessionCard()}
        ${this.wgCard()}
        ${this.appearanceCard()}
        ${when(this.pwOpen, () => this.pwDialog())}
      </div>`;
  }

  private thresholdsCard(): SafeHtml {
    const canEdit = session.get().claims?.brands ?? false;
    const d = this.draft;
    if (!d) return html``;
    return html`
      <div class="card" style="flex:1 1 330px;min-width:0">
        <div style="display:flex;align-items:center;margin-bottom:10px">
          <p class="sect" style="margin:0">Sağlık eşikleri</p>
          ${when(!canEdit, () => html`
            <span class="chip" style="margin-left:auto;background:var(--vx-chip-bg);color:var(--vx-chip-fg)">
              <i class="ti ti-lock" style="font-size:12px"></i>salt okunur
            </span>`)}
        </div>
        <div style="display:flex;flex-direction:column;gap:14px">
          ${each(THRESHOLD_ROWS, (row) => html`
            <div>
              <div style="display:flex;align-items:center;gap:7px;margin-bottom:7px">
                <i class="${row.icon}" style="font-size:16px;color:var(--vx-text-2)"></i>
                <span style="font:500 12.5px var(--vx-font-sans)">${row.label}</span>
              </div>
              <div style="display:flex;gap:10px">
                <div style="flex:1;min-width:0">
                  <label class="lbl" style="color:var(--vx-warning-text)">Uyarı</label>
                  <input class="inp mono" data-act="th" data-key="${row.key}" data-field="warn"
                         value="${d[row.key].warn}" ${canEdit ? '' : 'readonly'}>
                </div>
                <div style="flex:1;min-width:0">
                  <label class="lbl" style="color:var(--vx-danger-text)">Kritik</label>
                  <input class="inp mono" data-act="th" data-key="${row.key}" data-field="crit"
                         value="${d[row.key].crit}" ${canEdit ? '' : 'readonly'}>
                </div>
              </div>
            </div>`)}
          ${when(canEdit, () => html`
            <button class="btn pri" style="width:100%" data-act="save"><i class="ti ti-device-floppy"></i>Eşikleri kaydet</button>`)}
        </div>
      </div>`;
  }

  private sessionCard(): SafeHtml {
    const user = session.get().user;
    if (!user) return html``;
    return html`
      <div class="card" style="flex:1 1 330px;min-width:0">
        <p class="sect">Oturum</p>
        <div style="display:flex;align-items:center;gap:11px;margin-bottom:14px">
          <span style="width:40px;height:40px;border-radius:50%;background:var(--vx-brand-50);color:var(--vx-brand-700);display:flex;align-items:center;justify-content:center;font:600 13px var(--vx-font-sans);flex:none">${initials(user.username)}</span>
          <div style="flex:1;min-width:0">
            <div style="font:600 13.5px var(--vx-font-sans);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${user.username}</div>
            <div style="font:400 11.5px var(--vx-font-sans);color:var(--vx-text-3)">${ROLE_LABELS[user.role]}</div>
          </div>
        </div>
        <div style="display:flex;flex-direction:column;gap:9px">
          <button class="btn sec" style="width:100%" data-act="openPw"><i class="ti ti-key"></i>Parola değiştir</button>
          <button class="btn dngo" style="width:100%" data-act="logout"><i class="ti ti-logout"></i>Oturumu kapat</button>
        </div>
      </div>`;
  }

  private pwDialog(): SafeHtml {
    return html`
      <div class="ovl menu" data-act="closePw" style="animation:fadeIn .15s">
        <div class="sheet" data-act="noop">
          <div style="padding:17px 20px;border-bottom:1px solid var(--vx-border);display:flex;align-items:center;gap:9px">
            <i class="ti ti-key" style="font-size:20px;color:var(--vx-primary)"></i>
            <span style="font:600 17px var(--vx-font-sans)">Parola değiştir</span>
          </div>
          <div style="padding:20px;display:flex;flex-direction:column;gap:13px">
            <div><label class="lbl">Mevcut parola</label>
              <input class="inp" type="password" data-act="pwCur" value="${this.pwCurrent}" autocomplete="current-password"></div>
            <div><label class="lbl">Yeni parola</label>
              <input class="inp" type="password" data-act="pwNew" value="${this.pwNext}" autocomplete="new-password"></div>
            <div><label class="lbl">Yeni parola (tekrar)</label>
              <input class="inp" type="password" data-act="pwNew2" value="${this.pwNext2}" autocomplete="new-password"></div>
            <div style="font:400 11px/1.4 var(--vx-font-sans);color:var(--vx-text-3)">Değişince bu oturum açık kalır, diğer cihazlardaki oturumların kapatılır.</div>
          </div>
          <div style="display:flex;justify-content:flex-end;gap:10px;padding:0 20px 20px">
            <button class="btn sec" data-act="closePw">İptal</button>
            <button class="btn pri" data-act="submitPw"><i class="ti ti-key"></i>Değiştir</button>
          </div>
        </div>
      </div>`;
  }

  private wgCard(): SafeHtml {
    const wg = settingsStore.get()?.wg;
    if (!wg) return html``;
    const rows = [
      { icon: 'ti ti-server', label: 'Sunucu adresi', value: wg.endpoint, color: 'var(--vx-text)' },
      { icon: 'ti ti-network', label: 'Tünel ağı', value: wg.network, color: 'var(--vx-text)' },
      {
        icon: 'ti ti-plug-connected',
        label: 'Bağlı cihaz',
        value: `${nfmt(wg.connected)} / ${nfmt(wg.total)}`,
        color: 'var(--vx-success-text)',
      },
    ];
    return html`
      <div class="card" style="flex:1 1 330px;min-width:0">
        <div style="display:flex;align-items:center;margin-bottom:10px">
          <p class="sect" style="margin:0">WireGuard</p>
          <span class="chip" style="margin-left:auto;background:var(--vx-chip-bg);color:var(--vx-chip-fg)">
            <i class="ti ti-lock" style="font-size:12px"></i>salt okunur
          </span>
        </div>
        <div style="display:flex;flex-direction:column;gap:2px">
          ${each(rows, (r) => html`
            <div style="display:flex;align-items:center;gap:10px;padding:10px 0;border-top:1px solid var(--vx-border)">
              <i class="${r.icon}" style="font-size:16px;color:var(--vx-text-3);flex:none"></i>
              <span style="font:400 12.5px var(--vx-font-sans);color:var(--vx-text-2)">${r.label}</span>
              <span class="mono" style="margin-left:auto;font:600 12.5px var(--vx-font-sans);color:${r.color};white-space:nowrap">${r.value}</span>
            </div>`)}
        </div>
      </div>`;
  }

  private appearanceCard(): SafeHtml {
    const current = themePreference.get();
    const prefs: ThemePreference[] = ['light', 'dark', 'system'];
    return html`
      <div class="card" style="flex:1 1 330px;min-width:0">
        <p class="sect">Görünüm</p>
        <div style="display:flex;gap:8px;flex-wrap:wrap">
          ${each(prefs, (p) => html`
            <button class="fchip ${current === p ? 'on' : ''}" data-act="theme" data-pref="${p}">
              <i class="${THEME_ICONS[p]}" style="font-size:14px"></i>${THEME_LABELS[p]}
            </button>`)}
        </div>
        <div style="font:400 11px/1.4 var(--vx-font-sans);color:var(--vx-text-3);margin-top:10px">
          "Sistem", işletim sisteminin açık/koyu tercihini izler. Seçim bu tarayıcıda saklanır.
        </div>
      </div>`;
  }
}

customElements.define('smg-settings-view', SettingsView);
